import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree } from "@angular/router";
import { JwtAuthService, UserRoleResponse } from "../services/auth/jwt-auth.service";
import { Observable, of } from "rxjs";
import { map, catchError } from "rxjs/operators";
import { RouteProtectionService } from "../services/route-protection.service";
import { TOTP_ENROLMENT_REQUIRED, TOTP_ENROLMENT_ROUTE } from "../constants/totp.constants";

@Injectable()
export class UserRoleGuard  {
  constructor(
    private router: Router,
    private jwtAuth: JwtAuthService,
    private routeProtectionService: RouteProtectionService,
  ) { }

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    let url: string = state.url;
    return this.checkAccess(next, url);
  }

  canActivateChild(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.canActivate(next, state);
  }

  checkAccess(route: ActivatedRouteSnapshot, url: any): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    if (this.jwtAuth.isLoggedIn()) {
      // 🔴 The enrolment screen is the one place a TOTP-held account is allowed to
      // reach, so it must not depend on an authenticated call. `VerifyTfa` 403s
      // everything except verify_tfa and totp/*, `getUserRole()` included -- and the
      // interceptor redirects here on that 403. Calling it for this route made the
      // redirect re-fire before the navigation committed (Router.url only updates on
      // completion, so the interceptor's `url !== ENROLMENT` guard still passed),
      // cancelling and restarting the navigation ~5 times a second until the 300/min
      // per-IP limiter tripped; from then on everything 429'd and the account landed
      // on /access-denied with no way to enrol. Measured 2026-09-09: 300-309 requests
      // a minute, sustained. Returning true here without a call breaks that loop --
      // the backend still authenticates every request the screen makes.
      // It sits *inside* the signed-in check on purpose: that check is
      // `!!getJwtToken()`, synchronous and local, so keeping it cannot re-create the
      // storm, and skipping it would render this screen to a signed-out visitor.
      if (url === TOTP_ENROLMENT_ROUTE) {
        return true;
      }

      return this.jwtAuth.getUserRole().pipe(
        map((userRoleResponse: UserRoleResponse) => {
          const role = userRoleResponse.profiletype;

          /** @ts-ignore */
          //DO NOT REMOVE ts-ignore. This is a bit of a hack to get current router state url.
          const path = route._routerState.url
          if (route.data.role && route.data.role.indexOf(role) === -1) {
            this.router.navigate(['/access-denied']);
            return false;
          } else if (this.routeProtectionService.cannotAccessRoute(path)) {
            this.router.navigate(['/access-denied']);
            return false;
          }
          return true;
        }),
        catchError((error) => {
          // The hold's own 403 is not an access failure -- the interceptor is
          // already sending this account to the enrolment route. Navigating to
          // /access-denied here would both strand them and cancel that redirect.
          if (error?.status === 403 && error?.error?.message === TOTP_ENROLMENT_REQUIRED) {
            return of(false);
          }
          this.router.navigate(['/access-denied']);
          return of(false);
        })
      );
    }

    this.router.navigate(["/sessions/signin"], {
      queryParams: {
        return: url
      }
    });
    return false;
  }
}
